import { useState, useRef } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import {
  useSEOMeta,
  generateFAQPageSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateLocalBusinessSchema,
} from "@/components/SEO";

const contactDetails = [
  {
    icon: "ri-map-pin-2-line",
    label: "Headquarters",
    lines: ["Clerks quarters opposite express exchange", "Buea, South West Region", "Cameroon"],
    action: null,
  },
  {
    icon: "ri-phone-line",
    label: "Phone",
    lines: ["+237 670 396 340"],
    action: "tel:+237670396340",
  },
  {
    icon: "ri-mail-line",
    label: "Email",
    lines: ["info@bhodevcameroon.org"],
    action: "mailto:info@bhodevcameroon.org",
  },
  {
    icon: "ri-time-line",
    label: "Office Hours",
    lines: ["Monday – Friday: 8:00 AM – 5:00 PM", "Saturday: 9:00 AM – 1:00 PM", "Sunday: Closed"],
    action: null,
  },
];

const socials = [
  { href: "https://web.facebook.com/profile.php?id=100094000555791", icon: "ri-facebook-fill", label: "Facebook", color: "hover:bg-[#1877F2]" },
  { href: "https://www.linkedin.com/in/bogi-health-organization-for-development-bhodev-805471276/", icon: "ri-linkedin-fill", label: "LinkedIn", color: "hover:bg-[#0A66C2]" },
  { href: "https://twitter.com/bhodevCameroon", icon: "ri-twitter-x-fill", label: "Twitter/X", color: "hover:bg-stone-800" },
  { href: "https://www.instagram.com/bhodevcameroon_ngo/", icon: "ri-instagram-line", label: "Instagram", color: "hover:bg-[#E1306C]" },
  { href: "https://www.youtube.com/@bhodevcameroon", icon: "ri-youtube-fill", label: "YouTube", color: "hover:bg-[#FF0000]" },
];

const faqs = [
  {
    q: "How can I donate to BHODEV?",
    a: "You can donate directly through our website's donation section. We accept various payment methods and all contributions go directly to our health and development programs in Cameroon.",
  },
  {
    q: "Can I volunteer remotely?",
    a: "Yes! We welcome remote volunteers in areas like communications, research, fundraising, and digital marketing. Visit our Get Involved page to apply.",
  },
  {
    q: "How do I partner with BHODEV?",
    a: "We welcome partnerships with organizations, institutions, and corporations. Please reach out via email at info@bhodevcameroon.org with your partnership proposal.",
  },
  {
    q: "Where does BHODEV operate?",
    a: "BHODEV primarily operates in Cameroon, with a focus on the South West Region. Our programs span health, education, economic security, and community protection.",
  },
];

function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    const formData = new URLSearchParams();
    const data = new FormData(formRef.current);
    data.forEach((value, key) => formData.append(key, value as string));
    setLoading(true);
    try {
      await fetch("https://readdy.ai/api/form/d7o7fialdqqj9kfl6630", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-secondary rounded-full mb-4">
          <i className="ri-check-line text-primary text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold text-stone-800 mb-2 font-saria">Message Sent!</h3>
        <p className="text-stone-500 text-sm max-w-sm">Thank you for reaching out to BHODEV. We&apos;ll get back to you within 2–3 business days.</p>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Full Name *</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-user-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="text"
              name="name"
              required
              placeholder="Your full name"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Email Address *</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="email"
              name="email"
              required
              placeholder="your@email.com"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Phone Number</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="tel"
              name="phone"
              placeholder="+237 000 000 000"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Subject *</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-chat-1-line text-stone-400 text-sm"></i>
            </div>
            <select
              name="subject"
              required
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 appearance-none cursor-pointer"
            >
              <option value="">Select a subject</option>
              <option value="General Inquiry">General Inquiry</option>
              <option value="Partnership">Partnership Opportunity</option>
              <option value="Donation">Donation Inquiry</option>
              <option value="Media & Press">Media &amp; Press</option>
              <option value="Volunteering">Volunteering</option>
              <option value="Research">Research Collaboration</option>
              <option value="Other">Other</option>
            </select>
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
              <i className="ri-arrow-down-s-line text-stone-400 text-sm"></i>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Organization / Institution</label>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-building-line text-stone-400 text-sm"></i>
          </div>
          <input
            type="text"
            name="organization"
            placeholder="Your organization (optional)"
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Message *</label>
        <textarea
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="Write your message here..."
          onChange={(e) => setCharCount(e.target.value.length)}
          className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:border-primary focus:bg-white transition-all duration-300 text-stone-800 placeholder-stone-400 resize-none"
        />
        <p className="text-xs text-stone-400 text-right">{charCount}/500</p>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="bg-primary text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary/20 flex items-center justify-center gap-2 self-end"
      >
        {loading ? (
          <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
        ) : (
          <><i className="ri-send-plane-line"></i> Send Message</>
        )}
      </button>
    </form>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-stone-100 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-stone-50 transition-colors duration-200 cursor-pointer"
      >
        <span className="text-stone-800 font-semibold text-sm pr-4">{q}</span>
        <div className="w-6 h-6 flex items-center justify-center shrink-0">
          <i className={`${open ? "ri-subtract-line" : "ri-add-line"} text-primary text-base transition-transform duration-300`}></i>
        </div>
      </button>
      {open && (
        <div className="px-6 pb-4">
          <p className="text-stone-500 text-sm leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function ContactPage() {
  useSEOMeta({
    title: "Contact Us - BHODEV Cameroon | Buea, South West Region",
    description:
      "Get in touch with BHODEV Cameroon. Find our Buea headquarters address, phone number, email, and office hours. We welcome partnerships, donations, and inquiries.",
    keywords:
      "BHODEV contact, Buea NGO contact, Cameroon NGO phone number, donate BHODEV, partner with BHODEV, volunteer Cameroon, info@bhodevcameroon.org",
    canonicalUrl: "https://bhodevcameroon.org/contact",
    ogImage:
      "https://readdy.ai/api/search-image?query=modern%20NGO%20office%20building%20in%20Buea%20Cameroon%20professional%20workspace%20African%20community%20workers%20bright%20natural%20light%20clean%20minimalist%20warm%20tones%20wide%20landscape&width=1200&height=630&seq=contact-og&orientation=landscape",
    schemaData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          ...generateWebPageSchema(
            "Contact Us - BHODEV Cameroon",
            "Get in touch with BHODEV Cameroon. Find our Buea headquarters address, phone number, email, and office hours.",
            "https://bhodevcameroon.org/contact"
          ),
        },
        {
          ...generateLocalBusinessSchema(
            "Bogi Health Organization for Development (BHODEV)",
            "BHODEV is a non-governmental organization in Buea, Cameroon working to promote health, peace, social justice and sustainable development since 2014.",
            "https://bhodevcameroon.org/contact",
            "https://readdy.ai/api/search-image?query=modern%20NGO%20office%20building%20in%20Buea%20Cameroon%20professional%20workspace%20African%20community%20workers%20bright%20natural%20light%20clean%20minimalist%20warm%20tones%20wide%20landscape&width=1200&height=630&seq=contact-og&orientation=landscape",
            "+237-670-396-340",
            "info@bhodevcameroon.org",
            "Clerks quarters opposite express exchange",
            "Buea",
            "South West Region",
            "CM",
            4.1527,
            9.2333,
            [
              { dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "17:00" },
              { dayOfWeek: "Saturday", opens: "09:00", closes: "13:00" },
            ]
          ),
        },
        {
          ...generateFAQPageSchema(faqs),
        },
        {
          ...generateBreadcrumbSchema([
            { name: "Home", url: "https://bhodevcameroon.org/" },
            { name: "Contact Us", url: "https://bhodevcameroon.org/contact" },
          ]),
        },
      ],
    },
  });

  const heroRef = useScrollAnimation();
  const infoRef = useScrollAnimation();
  const formSectionRef = useScrollAnimation();
  const faqRef = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[480px] md:min-h-[540px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frehabilitation-of-water-catchment-in-wonya-mavio%2FIMG-20230706-WA0017.jpg?alt=media&token=2040f2e6-9187-44d9-b0c0-eb72071c96b3"
            alt="Contact BHODEV - Buea Cameroon NGO Office"
            title="Contact BHODEV - Buea Cameroon NGO Office"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-dark/85 via-dark/65 to-dark/25"></div>
        </div>
        <div ref={heroRef} className="relative z-10 px-4 md:px-8 lg:px-16 pt-28 pb-16 w-full">
          <div className="max-w-2xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-primary/20 backdrop-blur-sm border border-primary/30 text-primary px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
              <i className="ri-map-pin-line"></i>
              Buea, Cameroon
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-saria leading-tight mb-6">
              Contact Us.
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-xl">
              Here you can find emails, phone numbers, and street addresses for Bogi Health Organization for Development Cameroon. We&apos;re always happy to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-16 bg-stone-50">
        <div ref={infoRef} className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactDetails.map((item, i) => (
              <div
                key={item.label}
                className="bg-white rounded-2xl p-6 border border-stone-100 hover:border-primary/30 hover:-translate-y-1 transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-secondary rounded-xl mb-4">
                  <i className={`${item.icon} text-primary text-xl`}></i>
                </div>
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">{item.label}</p>
                {item.lines.map((line, j) =>
                  item.action && j === 0 ? (
                    <a
                      key={j}
                      href={item.action}
                      className="block text-stone-700 text-sm font-medium hover:text-primary transition-colors duration-200"
                    >
                      {line}
                    </a>
                  ) : (
                    <p key={j} className="text-stone-600 text-sm leading-relaxed">{line}</p>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map + Form */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-16">
        <div ref={formSectionRef} className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
            {/* Left: Map + Social */}
            <div className="lg:col-span-2 flex flex-col gap-8 animate-slide-in-left">
              <div>
                <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-2">Find Us</p>
                <h2 className="text-2xl md:text-3xl font-bold text-stone-800 font-saria mb-4">Our Location</h2>
                <p className="text-stone-500 text-sm leading-relaxed mb-6">
                  Visit us at our headquarters in Buea, South West Region, Cameroon. We&apos;re located at Clerks quarters, opposite express exchange.
                </p>
              </div>

              {/* Map */}
              <div className="rounded-2xl overflow-hidden border border-stone-100 h-64">
                <iframe
                  title="BHODEV Location - Buea, Cameroon"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15924.123456789!2d9.2333!3d4.1527!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x10610d3b3b3b3b3b%3A0x0!2sBuea%2C%20Cameroon!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>

              {/* Social Links */}
              <div>
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-4">Follow Us</p>
                <div className="flex flex-wrap gap-3">
                  {socials.map((s) => (
                    <a
                      key={s.href}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      aria-label={s.label}
                      className={`flex items-center gap-2 bg-stone-100 text-stone-600 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 hover:text-white cursor-pointer ${s.color}`}
                    >
                      <i className={`${s.icon} text-sm`}></i>
                      {s.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="lg:col-span-3 animate-slide-in-right">
              <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-2">Get In Touch</p>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-800 font-saria mb-2">Send Us a Message</h2>
              <p className="text-stone-500 text-sm leading-relaxed mb-8">
                Whether you have a question, a partnership proposal, or just want to say hello — we&apos;d love to hear from you. We typically respond within 2–3 business days.
              </p>
              <div className="bg-white rounded-3xl border border-stone-100 p-6 md:p-8">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-16 bg-stone-50">
        <div ref={faqRef} className="max-w-3xl mx-auto animate-fade-in-up">
          <div className="text-center mb-12">
            <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3">FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 font-saria mb-4">Frequently Asked Questions</h2>
            <p className="text-stone-500 text-sm leading-relaxed">
              Can&apos;t find what you&apos;re looking for? Reach out to us directly.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white font-saria mb-4">Ready to Make a Difference?</h2>
          <p className="text-white/80 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
            Join BHODEV in our mission to promote health, peace, social justice and sustainable development in Cameroon.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="/get-involved"
              className="bg-white text-primary px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-stone-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Involved
            </a>
            <a
              href="/#donate"
              className="bg-white/10 border border-white/30 text-white px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Donate Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}